import { countRu } from './pluralRu';

/*
 * NAV-ENTITLEMENTS01 (ТЗ v4 §31, FE-FIX28): «Видимые вкладки».
 *
 * Навигация — это presentation, а не авторизация. Скрытый пункт меню ничего не
 * запрещает: прямой URL всё равно упирается в backend 403/404, и наоборот —
 * видимый пункт не выдаёт доступ к данным. Поэтому здесь только работа с
 * ключами: ключи стабильны, подписи к ним — локальные и никогда не служат
 * идентификатором права.
 *
 * Неизвестный ключ от backend игнорируется (fail-closed по отображению): мы
 * показываем только те пункты меню, которые знаем сами и которые backend
 * прислал как доступные.
 *
 * Правило родителя — self-or-descendant: родительский пункт виден, если выбран
 * он сам или любой его потомок. Видимость потомка при этом не даёт доступа к
 * данным родителя.
 */

const firstDefined = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const asList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.nodes)) return data.nodes;
  if (Array.isArray(data?.tree)) return data.tree;
  if (Array.isArray(data?.navigation)) return data.navigation;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export const normalizeNavigationTree = (data) => {
  const walk = (items) => asList(items)
    .map((item) => {
      if (typeof item === 'string') return { key: item, label: item, adminOnly: false, children: [] };
      const key = firstDefined(item, ['key', 'navigation_key', 'id', 'code']);
      if (key === undefined) return null;
      return {
        key: String(key),
        label: String(firstDefined(item, ['label', 'title', 'display_name', 'name']) ?? key),
        adminOnly: Boolean(item.admin_only),
        children: walk(firstDefined(item, ['children', 'items', 'nodes']) || []),
      };
    })
    .filter(Boolean);
  return walk(data);
};

export const normalizeUserNavigation = (data) => ({
  visibleKeys: (firstDefined(data, ['visible_keys', 'visible_tabs', 'keys']) || []).map(String),
  version: firstDefined(data, ['version', 'navigation_version']) ?? null,
  // Админ неизменяем по контракту: редактировать его навигацию нельзя.
  // Backend помечает это как mode=admin_all / role=admin.
  immutable: Boolean(firstDefined(data, ['immutable', 'is_immutable', 'readonly', 'is_admin']))
    || data?.mode === 'admin_all'
    || data?.role === 'admin',
});

export const flattenNavigationKeys = (tree) => {
  const out = [];
  const walk = (nodes) => nodes.forEach((node) => {
    out.push(node.key);
    walk(node.children || []);
  });
  walk(tree || []);
  return out;
};

const descendantKeys = (node) => {
  const out = [];
  const walk = (nodes) => (nodes || []).forEach((child) => {
    out.push(child.key);
    walk(child.children);
  });
  walk(node?.children);
  return out;
};

export const findNavigationNode = (tree, key) => {
  const stack = [...(tree || [])];
  while (stack.length > 0) {
    const node = stack.shift();
    if (node.key === key) return node;
    stack.unshift(...(node.children || []));
  }
  return null;
};

/** Пункт виден пользователю, если выбран он сам или любой его потомок. */
export const isNavigationNodeVisible = (node, selectedKeys) => {
  const selected = new Set(selectedKeys || []);
  if (selected.has(node.key)) return true;
  return descendantKeys(node).some((key) => selected.has(key));
};

/** Частично выбранный родитель: сам не выбран, но выбран кто-то из потомков. */
export const isNavigationNodePartial = (node, selectedKeys) => {
  const selected = new Set(selectedKeys || []);
  if (selected.has(node.key)) return false;
  return descendantKeys(node).some((key) => selected.has(key));
};

/**
 * Клик по пункту. Снятие родителя убирает и его потомков: иначе пункт остался бы
 * видимым из-за правила self-or-descendant, а чекбокс показывал бы обратное.
 */
export const toggleNavigationKey = (selectedKeys, node) => {
  const selected = new Set(selectedKeys || []);
  if (selected.has(node.key)) {
    selected.delete(node.key);
    descendantKeys(node).forEach((key) => selected.delete(key));
  } else {
    selected.add(node.key);
  }
  return Array.from(selected);
};

export const sameNavigationSelection = (a, b) => {
  const left = [...new Set(a || [])].sort();
  const right = [...new Set(b || [])].sort();
  return left.length === right.length && left.every((key, index) => key === right[index]);
};

/**
 * Пункты меню, которые реально показываем. Ключи backend — это не id маршрутов
 * (`monitoring.frames`, `events.list`, …), поэтому пункт меню сам перечисляет
 * в `navKeys`, какие ключи его открывают: достаточно любого. Пункт без
 * `navKeys` в каталоге backend не представлен (помощь, OEE) и виден всегда.
 * Ключ, которого не знает ни один пункт, игнорируется — из подписи нельзя
 * сделать право.
 */
export const filterMenuByNavigation = (items, navigationKeys) => {
  if (!Array.isArray(navigationKeys)) return items;
  const keys = navigationKeys.map(String);
  return items.filter((item) => !Array.isArray(item.navKeys)
    || item.navKeys.some((key) => isNavigationKeyAllowed(keys, key)));
};

/** Узлы, которые можно выдать обычному пользователю: admin_only отдаёт только роль. */
export const withoutAdminOnly = (tree) => (tree || [])
  .filter((node) => !node.adminOnly)
  .map((node) => ({ ...node, children: withoutAdminOnly(node.children) }));

export const describeVisibleTabs = (visibleKeys, total) => {
  const count = new Set(visibleKeys || []).size;
  if (!total) return countRu(count, ['вкладка', 'вкладки', 'вкладок']);
  if (count >= total) return `Все вкладки (${total})`;
  return `${count} из ${total}`;
};

/*
 * Каталог backend устроен не так, как меню фронта: «Мониторинг» — это раздел
 * «Камеры», «Операционные проблемы» — вкладка «Системные проблемы» в
 * «Сводке», а «Оборудование» лежит внутри «Мониторинга», хотя в меню это
 * отдельный раздел. В «Ролях» администратор должен видеть те же названия и ту
 * же группировку, что и в интерфейсе, поэтому дерево перед показом
 * переименовывается и перегруппировывается.
 *
 * Меняется только представление: ключи остаются ключами backend, и в PUT
 * уходят ровно они. Неизвестные ключи показываются как есть, с подписью
 * backend.
 */
export const NAVIGATION_LABELS = {
  summary: 'Сводка',
  'summary.overview': 'Нарушения',
  'summary.business_metrics': 'Бизнес-метрики',
  'analytics.operational_issues': 'Системные проблемы',
  monitoring: 'Камеры',
  'monitoring.frames': 'Кадры',
  'monitoring.live': 'Прямой эфир',
  'monitoring.archive': 'Архив',
  'monitoring.equipment': 'Оборудование',
  analytics: 'Статистика',
  'analytics.statistics': 'Общая статистика',
  'analytics.gantt': 'Диаграмма Ганта',
  events: 'События',
  'events.list': 'Список событий',
  profile: 'Личный кабинет',
  'profile.control': 'Контроль',
};

/** Куда узел переносится в дереве «Ролей»: ключ родителя или '' — верхний уровень. */
const NAVIGATION_REPARENT = {
  'analytics.operational_issues': 'summary',
  'monitoring.equipment': '',
};

/** Порядок разделов — как в меню сайдбара. */
const NAVIGATION_ORDER = ['profile', 'monitoring', 'summary', 'events', 'analytics', 'monitoring.equipment', 'admin'];

export const presentNavigationTree = (tree) => {
  const moved = [];
  const strip = (nodes) => (nodes || [])
    .filter((node) => {
      if (Object.prototype.hasOwnProperty.call(NAVIGATION_REPARENT, node.key)) {
        moved.push(node);
        return false;
      }
      return true;
    })
    .map((node) => ({
      ...node,
      label: NAVIGATION_LABELS[node.key] || node.label,
      children: strip(node.children),
    }));

  const roots = strip(tree);
  moved.forEach((node) => {
    const presented = { ...node, label: NAVIGATION_LABELS[node.key] || node.label, children: strip(node.children) };
    const parentKey = NAVIGATION_REPARENT[node.key];
    const parent = parentKey ? roots.find((root) => root.key === parentKey) : null;
    if (parent) parent.children = [...parent.children, presented];
    else roots.push(presented);
  });

  const rank = (key) => {
    const index = NAVIGATION_ORDER.indexOf(key);
    return index < 0 ? NAVIGATION_ORDER.length : index;
  };
  return roots
    .map((node, index) => ({ node, index }))
    .sort((a, b) => rank(a.node.key) - rank(b.node.key) || a.index - b.index)
    .map(({ node }) => node);
};

/**
 * Видна ли вкладка страницы. keys = null — навигация не загружена или backend
 * её не поддерживает: ограничений нет (как и у сайдбара).
 */
export const isNavigationKeyAllowed = (keys, key) => !Array.isArray(keys) || keys.includes(key);

/** Потомки родительских ключей в каталоге v2 (live granted_keys). */
const LEGACY_CHILD_KEYS = {
  summary: ['summary.overview', 'summary.business_metrics'],
  monitoring: ['monitoring.frames', 'monitoring.live', 'monitoring.archive', 'monitoring.equipment'],
  analytics: ['analytics.statistics', 'analytics.gantt', 'analytics.operational_issues'],
  events: ['events.list', 'events.rules', 'events.change_requests', 'events.camera_settings'],
  profile: ['profile.control'],
  admin: ['admin.hierarchy', 'admin.cameras', 'admin.rules', 'admin.roles'],
};

/**
 * Ответ старого каталога (catalog_version не задан или < 2) перечисляет только
 * родителей: родитель без единого потомка в списке означает «все его вкладки».
 * В каталоге v2 список точный (['profile'] — профиль без «Контроля»), поэтому
 * там ключи не меняются.
 */
export const expandLegacyNavigationKeys = (keys, catalogVersion) => {
  const list = Array.isArray(keys) ? keys : [];
  if (catalogVersion != null && Number(catalogVersion) >= 2) return list;
  const out = [...list];
  Object.entries(LEGACY_CHILD_KEYS).forEach(([parent, children]) => {
    if (list.includes(parent) && !list.some((key) => key.startsWith(`${parent}.`))) {
      out.push(...children);
    }
  });
  return out;
};

/**
 * Ключи каталога навигации v2 (catalog_version 2), которыми фронт гейтит
 * вкладки страниц. Родительский пункт сайдбара открывается любым из потомков
 * (см. navKeys в sidebarMenu.js); неизвестные ключи backend игнорируются.
 */
export const NAV_KEYS = {
  SUMMARY: 'summary',
  SUMMARY_OVERVIEW: 'summary.overview',
  SUMMARY_BUSINESS_METRICS: 'summary.business_metrics',
  EVENTS: 'events',
  EVENTS_LIST: 'events.list',
  EVENTS_RULES: 'events.rules',
  EVENTS_CHANGE_REQUESTS: 'events.change_requests',
  EVENTS_CAMERA_SETTINGS: 'events.camera_settings',
  ANALYTICS_OPERATIONAL_ISSUES: 'analytics.operational_issues',
  MONITORING: 'monitoring',
  MONITORING_FRAMES: 'monitoring.frames',
  MONITORING_EQUIPMENT: 'monitoring.equipment',
  MONITORING_LIVE: 'monitoring.live',
  MONITORING_ARCHIVE: 'monitoring.archive',
  ANALYTICS: 'analytics',
  PROFILE: 'profile',
};
